import { useState } from "react";
import ChurnPanel from "./ChurnPanel";

export interface Subscription {
  id: string;
  email: string;
  plan_id: string;
  status: string;
  billing: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  created_at: string;
  assigned_phone: string | null;
  setup_completed: boolean;
  preferred_lang: string | null;
  admin_notes: string | null;
  cancellation_reason: string | null;
}

const CANCELLATION_LABELS: Record<string, { label: string; emoji: string }> = {
  precio:           { label: "Precio alto",              emoji: "💰" },
  "sin-uso":        { label: "No lo usó",                emoji: "⚙️" },
  tecnico:          { label: "Problemas técnicos",       emoji: "🔧" },
  funcionalidades:  { label: "Faltan funcionalidades",   emoji: "🧩" },
  competidor:       { label: "Fue a la competencia",     emoji: "🔄" },
  "sin-necesidad":  { label: "Ya no lo necesita",        emoji: "📵" },
  "too-expensive":  { label: "Precio alto",              emoji: "💰" },
  "didnt-use":      { label: "No lo usó",                emoji: "⚙️" },
  technical:        { label: "Problemas técnicos",       emoji: "🔧" },
  "missing-features": { label: "Faltan funcionalidades", emoji: "🧩" },
  competitor:       { label: "Fue a la competencia",     emoji: "🔄" },
  "no-need":        { label: "Ya no lo necesita",        emoji: "📵" },
};

interface Props {
  subscriptions: Subscription[];
  loading: boolean;
  onAssignPhone: (id: string, email: string, planId: string, phone: string, lang: string) => Promise<void>;
  onSaveNote: (id: string, note: string) => Promise<void>;
  onSendReminder: (id: string, email: string, planId: string, assignedPhone: string | null, lang: string) => Promise<void>;
  externalFilter?: FilterType;
}

type FilterType = "all" | "no_phone" | "setup_pending" | "canceled";

function formatDate(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" });
}

function statusBadge(status: string, cancelPending: boolean) {
  if (cancelPending) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 text-amber-700 text-xs rounded-lg font-medium">
        <span className="w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0" />
        Cancela al vencer
      </span>
    );
  }
  if (status === "active") {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 text-emerald-700 text-xs rounded-lg font-medium">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
        Activa
      </span>
    );
  }
  if (status === "past_due") {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-rose-50 text-rose-700 text-xs rounded-lg font-medium">
        <span className="w-1.5 h-1.5 rounded-full bg-rose-500 flex-shrink-0" />
        Pago fallido
      </span>
    );
  }
  if (status === "canceled") {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-stone-100 text-stone-500 text-xs rounded-lg font-medium">
        <span className="w-1.5 h-1.5 rounded-full bg-stone-400 flex-shrink-0" />
        Cancelada
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-stone-100 text-stone-600 text-xs rounded-lg font-medium">
      {status}
    </span>
  );
}

function planLabel(planId: string) {
  const plans: Record<string, string> = {
    starter: "Starter", professional: "Professional", enterprise: "Enterprise",
    basico: "Básico", profesional: "Profesional", premium: "Premium",
  };
  return plans[planId?.toLowerCase()] || planId || "—";
}

function billingLabel(billing: string | null) {
  if (!billing) return "—";
  return billing === "annual" || billing === "yearly" ? "Anual" : "Mensual";
}

// ── Phone cell ────────────────────────────────────────────────
interface PhoneCellProps {
  sub: Subscription;
  onAssignPhone: Props["onAssignPhone"];
  onNotified: (email: string) => void;
}

function PhoneCell({ sub, onAssignPhone, onNotified }: PhoneCellProps) {
  const [editing, setEditing] = useState(false);
  const [phoneInput, setPhoneInput] = useState(sub.assigned_phone ?? "");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSave = async () => {
    const trimmed = phoneInput.trim();
    if (!trimmed) return;
    setSaving(true);
    try {
      await onAssignPhone(sub.id, sub.email, sub.plan_id, trimmed, sub.preferred_lang ?? "es");
      setSaved(true);
      setEditing(false);
      onNotified(sub.email);
      setTimeout(() => setSaved(false), 3000);
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div className="flex items-center gap-2 min-w-[200px]">
        <input
          type="text"
          value={phoneInput}
          onChange={(e) => setPhoneInput(e.target.value)}
          placeholder="+34 XXX XXX XXX"
          className="flex-1 px-2.5 py-1.5 text-xs border border-amber-300 rounded-lg outline-none focus:ring-1 focus:ring-amber-300 bg-amber-50 text-stone-800 font-mono"
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSave();
            if (e.key === "Escape") { setEditing(false); setPhoneInput(sub.assigned_phone ?? ""); }
          }}
          autoFocus
        />
        <button
          onClick={handleSave}
          disabled={saving || !phoneInput.trim()}
          className="w-7 h-7 flex items-center justify-center rounded-lg bg-emerald-500 hover:bg-emerald-600 disabled:bg-stone-200 text-white transition-colors cursor-pointer"
          title="Guardar y notificar cliente"
        >
          {saving ? <i className="ri-loader-4-line animate-spin text-xs" /> : <i className="ri-check-line text-xs" />}
        </button>
        <button
          onClick={() => { setEditing(false); setPhoneInput(sub.assigned_phone ?? ""); }}
          className="w-7 h-7 flex items-center justify-center rounded-lg bg-stone-100 hover:bg-stone-200 text-stone-500 transition-colors cursor-pointer"
        >
          <i className="ri-close-line text-xs" />
        </button>
      </div>
    );
  }

  if (sub.assigned_phone) {
    return (
      <div className="flex items-center gap-2 group">
        <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-mono font-semibold ${saved ? "bg-emerald-50 text-emerald-700" : "bg-stone-900 text-amber-400"}`}>
          {saved && <i className="ri-check-line text-emerald-500 text-xs" />}
          {sub.assigned_phone}
        </div>
        <button
          onClick={() => { setPhoneInput(sub.assigned_phone ?? ""); setEditing(true); }}
          className="w-6 h-6 flex items-center justify-center rounded-md bg-stone-100 hover:bg-amber-100 text-stone-400 hover:text-amber-600 transition-colors cursor-pointer opacity-0 group-hover:opacity-100"
          title="Editar número"
        >
          <i className="ri-pencil-line text-xs" />
        </button>
      </div>
    );
  }

  return (
    <button
      onClick={() => setEditing(true)}
      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-amber-50 hover:bg-amber-100 border border-amber-200 border-dashed rounded-lg text-xs font-semibold text-amber-600 transition-colors cursor-pointer whitespace-nowrap"
    >
      <i className="ri-phone-line text-xs" />
      Asignar número
    </button>
  );
}

// ── Notes cell ────────────────────────────────────────────────
interface NotesPanelProps {
  sub: Subscription;
  onSaveNote: Props["onSaveNote"];
}

function NotesPanel({ sub, onSaveNote }: NotesPanelProps) {
  const [text, setText] = useState(sub.admin_notes ?? "");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const dirty = text !== (sub.admin_notes ?? "");

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSaveNote(sub.id, text);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="px-6 pb-5 pt-0">
      <div className="ml-11 border-t border-stone-100 pt-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-bold text-stone-500 flex items-center gap-1.5">
            <i className="ri-sticky-note-line text-amber-400" />
            Notas internas
          </p>
          {saved && (
            <span className="text-xs text-emerald-500 font-semibold flex items-center gap-1">
              <i className="ri-check-line" />
              Guardado
            </span>
          )}
        </div>
        <textarea
          value={text}
          onChange={(e) => { setText(e.target.value); setSaved(false); }}
          placeholder="Añade notas internas sobre este cliente: estado del onboarding, incidencias, conversaciones importantes..."
          rows={3}
          maxLength={1000}
          className="w-full px-3 py-2.5 text-xs bg-stone-50 border border-stone-200 rounded-xl text-stone-700 placeholder-stone-300 focus:outline-none focus:border-amber-300 focus:ring-1 focus:ring-amber-100 transition-all resize-none"
        />
        <div className="flex items-center justify-between mt-2">
          <span className="text-xs text-stone-300">{text.length}/1000</span>
          <button
            onClick={handleSave}
            disabled={saving || !dirty}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-stone-900 hover:bg-stone-700 disabled:bg-stone-100 disabled:text-stone-400 text-white text-xs font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            {saving ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-save-line" />}
            Guardar nota
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Reminder button ────────────────────────────────────────────
interface ReminderButtonProps {
  sub: Subscription;
  onSendReminder: Props["onSendReminder"];
}

function ReminderButton({ sub, onSendReminder }: ReminderButtonProps) {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [sentAt, setSentAt] = useState<string | null>(null);
  const [error, setError] = useState(false);

  if (sub.setup_completed) return null;

  const handleSend = async () => {
    if (sending || sent) return;
    setSending(true);
    setError(false);
    try {
      await onSendReminder(sub.id, sub.email, sub.plan_id, sub.assigned_phone, sub.preferred_lang ?? "es");
      setSent(true);
      setSentAt(new Date().toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" }));
    } catch {
      setError(true);
      setTimeout(() => setError(false), 3000);
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-emerald-50 border border-emerald-100 rounded-lg">
        <i className="ri-check-line text-emerald-500 text-xs" />
        <span className="text-xs font-semibold text-emerald-600 whitespace-nowrap">Enviado {sentAt}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-rose-50 border border-rose-100 rounded-lg">
        <i className="ri-error-warning-line text-rose-500 text-xs" />
        <span className="text-xs font-semibold text-rose-600 whitespace-nowrap">Error al enviar</span>
      </div>
    );
  }

  return (
    <button
      onClick={handleSend}
      disabled={sending}
      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-orange-50 hover:bg-orange-100 border border-orange-200 rounded-lg text-xs font-semibold text-orange-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
      title="Enviar email de recordatorio para completar la configuración"
    >
      {sending ? (
        <i className="ri-loader-4-line animate-spin text-xs" />
      ) : (
        <i className="ri-mail-send-line text-xs" />
      )}
      {sending ? "Enviando..." : "Enviar recordatorio"}
    </button>
  );
}

// ── Main component ────────────────────────────────────────────
export default function SubscriptionsTable({ subscriptions, loading, onAssignPhone, onSaveNote, onSendReminder, externalFilter }: Props) {
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<FilterType>("all");
  const [notifySent, setNotifySent] = useState<string | null>(null);
  const [expandedNotes, setExpandedNotes] = useState<Set<string>>(new Set());

  const activeFilter = externalFilter ?? filter;

  const setActiveFilter = (f: FilterType) => {
    setFilter(f);
  };

  const toggleNotes = (id: string) => {
    setExpandedNotes((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const filtered = subscriptions.filter((s) => {
    const matchSearch =
      s.email.toLowerCase().includes(search.toLowerCase()) ||
      (s.plan_id || "").toLowerCase().includes(search.toLowerCase());
    if (!matchSearch) return false;
    if (activeFilter === "no_phone") return !s.assigned_phone;
    if (activeFilter === "setup_pending") return !s.setup_completed;
    if (activeFilter === "canceled") return s.status === "canceled";
    return true;
  });

  const noPhoneCount = subscriptions.filter((s) => !s.assigned_phone).length;
  const setupPendingCount = subscriptions.filter((s) => !s.setup_completed).length;
  const canceledCount = subscriptions.filter((s) => s.status === "canceled").length;

  const FILTERS: { key: FilterType; label: string; count?: number; icon: string; warn?: boolean }[] = [
    { key: "all",           label: "Todos",           count: subscriptions.length, icon: "ri-list-check-2" },
    { key: "no_phone",      label: "Sin número",      count: noPhoneCount,         icon: "ri-phone-off-line",   warn: noPhoneCount > 0 },
    { key: "setup_pending", label: "Setup pendiente", count: setupPendingCount,    icon: "ri-settings-2-line",  warn: setupPendingCount > 0 },
    { key: "canceled",      label: "Canceladas",      count: canceledCount,        icon: "ri-close-circle-line", warn: false },
  ];

  return (
    <div className="bg-white border border-stone-100 rounded-2xl overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-stone-100">
        <div className="flex items-center justify-between gap-4 mb-4">
          <div>
            <h2 className="text-base font-bold text-stone-900">Suscripciones</h2>
            <p className="text-xs text-stone-400">{subscriptions.length} suscripciones en total</p>
          </div>
          <div className="relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
              <i className="ri-search-line text-stone-400 text-sm" />
            </div>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por email o plan..."
              className="pl-9 pr-4 py-2 border border-stone-200 rounded-xl text-sm text-stone-700 placeholder-stone-400 outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-50 transition-all w-64"
            />
          </div>
        </div>

        {/* Filter tabs */}
        <div className="flex items-center gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setActiveFilter(f.key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                activeFilter === f.key
                  ? "bg-stone-900 text-white"
                  : f.warn
                  ? "bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100"
                  : "bg-stone-100 text-stone-500 hover:bg-stone-200"
              }`}
            >
              <i className={`${f.icon} text-xs`} />
              {f.label}
              {f.count !== undefined && (
                <span className={`px-1.5 py-0.5 rounded-full text-xs font-bold ${
                  activeFilter === f.key
                    ? "bg-white/20 text-white"
                    : f.warn && f.count > 0
                    ? "bg-amber-200 text-amber-800"
                    : "bg-stone-200 text-stone-500"
                }`}>
                  {f.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Churn Panel — visible only when filtered by canceled */}
      {activeFilter === "canceled" && (
        <div className="px-6 pt-5">
          <ChurnPanel subscriptions={subscriptions} />
        </div>
      )}

      {/* Notification toast */}
      {notifySent && (
        <div className="mx-6 mt-3 flex items-center gap-3 px-4 py-3 bg-emerald-50 border border-emerald-100 rounded-xl text-sm text-emerald-700">
          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
            <i className="ri-mail-send-line text-emerald-500" />
          </div>
          <div>
            <strong>Email enviado</strong> — {notifySent} ha recibido la notificación con su número Auria y los códigos de desvío.
          </div>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-16 gap-3 text-stone-400">
          <i className="ri-loader-4-line animate-spin text-xl" />
          <span className="text-sm">Cargando suscripciones...</span>
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-stone-400">
          <div className="w-12 h-12 flex items-center justify-center">
            <i className="ri-vip-crown-line text-3xl" />
          </div>
          <p className="text-sm mt-2">{search || activeFilter !== "all" ? "Sin resultados con los filtros aplicados" : "Aún no hay suscripciones"}</p>
          {activeFilter !== "all" && (
            <button onClick={() => setActiveFilter("all")} className="mt-2 text-xs text-amber-600 hover:underline cursor-pointer">
              Ver todas
            </button>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-stone-50 border-b border-stone-100">
                <th className="text-left px-6 py-3 text-xs font-bold text-stone-500 uppercase tracking-wide">Email</th>
                <th className="text-left px-6 py-3 text-xs font-bold text-stone-500 uppercase tracking-wide">Plan</th>
                <th className="text-left px-6 py-3 text-xs font-bold text-stone-500 uppercase tracking-wide">Estado</th>
                <th className="text-left px-6 py-3 text-xs font-bold text-stone-500 uppercase tracking-wide">
                  <span className="flex items-center gap-1">
                    <i className="ri-phone-line" />
                    Número Auria
                  </span>
                </th>
                <th className="text-left px-6 py-3 text-xs font-bold text-stone-500 uppercase tracking-wide">Setup</th>
                <th className="text-left px-6 py-3 text-xs font-bold text-stone-500 uppercase tracking-wide">Vence</th>
                <th className="text-left px-6 py-3 text-xs font-bold text-stone-500 uppercase tracking-wide">Fact.</th>
                <th className="text-left px-6 py-3 text-xs font-bold text-stone-500 uppercase tracking-wide">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-50">
              {filtered.map((sub) => {
                const notesOpen = expandedNotes.has(sub.id);
                const hasNote = !!(sub.admin_notes && sub.admin_notes.trim());
                return (
                  <>
                    <tr key={sub.id} className="hover:bg-stone-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center flex-shrink-0">
                            <i className="ri-user-line text-stone-400 text-sm" />
                          </div>
                          <div className="min-w-0">
                            <span className="text-sm text-stone-700 font-medium block truncate max-w-[180px]">{sub.email}</span>
                            {sub.preferred_lang && (
                              <span className="text-xs text-stone-400">{sub.preferred_lang.toUpperCase()}</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center px-2.5 py-1 bg-stone-900 text-white text-xs rounded-lg font-bold whitespace-nowrap">
                          {planLabel(sub.plan_id)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col gap-1.5">
                          {statusBadge(sub.status, sub.cancel_at_period_end)}
                          {sub.status === "canceled" && sub.cancellation_reason && (() => {
                            const info = CANCELLATION_LABELS[sub.cancellation_reason];
                            return info ? (
                              <span className="inline-flex items-center gap-1 text-xs text-stone-400 font-medium">
                                <span>{info.emoji}</span>
                                <span>{info.label}</span>
                              </span>
                            ) : (
                              <span className="text-xs text-stone-400 truncate max-w-[120px]">{sub.cancellation_reason}</span>
                            );
                          })()}
                          {sub.status === "canceled" && !sub.cancellation_reason && (
                            <span className="text-xs text-stone-300 italic">Sin feedback</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <PhoneCell
                          sub={sub}
                          onAssignPhone={onAssignPhone}
                          onNotified={(email) => {
                            setNotifySent(email);
                            setTimeout(() => setNotifySent(null), 5000);
                          }}
                        />
                      </td>
                      <td className="px-6 py-4">
                        {sub.setup_completed ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 text-emerald-700 text-xs rounded-lg font-medium">
                            <i className="ri-check-double-line text-xs" />
                            Completado
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 text-amber-600 text-xs rounded-lg font-medium">
                            <i className="ri-time-line text-xs" />
                            Pendiente
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-stone-500 whitespace-nowrap">{formatDate(sub.current_period_end)}</td>
                      <td className="px-6 py-4 text-xs text-stone-500">{billingLabel(sub.billing)}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <button
                            onClick={() => toggleNotes(sub.id)}
                            className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors cursor-pointer relative ${
                              notesOpen
                                ? "bg-amber-100 text-amber-600"
                                : hasNote
                                ? "bg-amber-50 text-amber-500 hover:bg-amber-100"
                                : "bg-stone-50 hover:bg-stone-100 text-stone-400"
                            }`}
                            title={notesOpen ? "Cerrar notas" : "Ver / editar notas"}
                          >
                            <i className="ri-sticky-note-line text-sm" />
                            {hasNote && !notesOpen && (
                              <span className="absolute top-0.5 right-0.5 w-2 h-2 rounded-full bg-amber-400" />
                            )}
                          </button>
                          <a
                            href={`mailto:${sub.email}`}
                            className="w-8 h-8 flex items-center justify-center rounded-lg bg-stone-50 hover:bg-stone-100 transition-colors cursor-pointer"
                            title="Enviar email"
                          >
                            <i className="ri-mail-line text-stone-500 text-sm" />
                          </a>
                          <ReminderButton sub={sub} onSendReminder={onSendReminder} />
                        </div>
                      </td>
                    </tr>
                    {notesOpen && (
                      <tr key={`notes-${sub.id}`} className="bg-stone-50/50">
                        <td colSpan={8}>
                          <NotesPanel sub={sub} onSaveNote={onSaveNote} />
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
